/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useCallback, useRef, useEffect } from 'react';
import { bucketService } from '../services/api';
import toast from 'react-hot-toast';
import { ObjectInfo } from '../types';
import { UploadTask } from '../components/storage/UploadProgressPanel';

// Threshold for using chunked upload (10MB)
const CHUNKED_UPLOAD_THRESHOLD = 10 * 1024 * 1024;

// Upload configuration based on internet speed
interface UploadConfig {
  chunkSize: number;      // Size of each chunk in bytes
  concurrency: number;    // Number of parallel chunk uploads
  speedTier: 'slow' | 'medium' | 'fast' | 'very-fast';
}

// Speed tiers and their configurations
// Slow: < 10 Mbps, Medium: 10-50 Mbps, Fast: 50-100 Mbps, Very Fast: > 100 Mbps
const UPLOAD_CONFIGS: Record<string, UploadConfig> = {
  'slow': {
    chunkSize: 5 * 1024 * 1024,      // 5MB chunks
    concurrency: 2,                   // 2 parallel uploads
    speedTier: 'slow',
  },
  'medium': {
    chunkSize: 25 * 1024 * 1024,     // 25MB chunks
    concurrency: 4,                   // 4 parallel uploads
    speedTier: 'medium',
  },
  'fast': {
    chunkSize: 50 * 1024 * 1024,     // 50MB chunks
    concurrency: 6,                   // 6 parallel uploads
    speedTier: 'fast',
  },
  'very-fast': {
    chunkSize: 100 * 1024 * 1024,    // 100MB chunks
    concurrency: 10,                  // 10 parallel uploads
    speedTier: 'very-fast',
  },
};

// Estimate internet speed by measuring download time of a small test
const estimateInternetSpeed = async (): Promise<number> => {
  try {
    // Use a small image from a CDN to estimate speed (fallback to navigator if available)
    if ('connection' in navigator) {
      const conn = (navigator as any).connection;
      if (conn && conn.downlink) {
        // downlink is in Mbps
        return conn.downlink;
      }
    }

    // Fallback: measure time to fetch a known-size resource
    const testUrl = 'https://www.google.com/favicon.ico';
    const startTime = performance.now();
    const response = await fetch(testUrl, { cache: 'no-store', mode: 'no-cors' });
    const endTime = performance.now();

    // Estimate based on typical favicon size (~1KB) and time taken
    const timeTaken = (endTime - startTime) / 1000; // seconds
    const estimatedSize = 1024; // ~1KB
    const speedBps = estimatedSize / timeTaken;
    const speedMbps = (speedBps * 8) / (1024 * 1024);

    // This is a rough estimate, scale it up as actual uploads use more bandwidth
    return Math.min(speedMbps * 10, 1000); // Cap at 1000 Mbps
  } catch {
    // Default to medium speed if we can't measure
    return 25;
  }
};

// Get upload config based on speed in Mbps
const getUploadConfig = (speedMbps: number): UploadConfig => {
  if (speedMbps < 10) {
    return UPLOAD_CONFIGS['slow'];
  } else if (speedMbps < 50) {
    return UPLOAD_CONFIGS['medium'];
  } else if (speedMbps < 100) {
    return UPLOAD_CONFIGS['fast'];
  } else {
    return UPLOAD_CONFIGS['very-fast'];
  }
};

interface ChunkUploadState {
  uploadId: string;
  path: string;
  parts: { part_number: number; etag: string }[];
  totalChunks: number;
  completedChunks: number;
  chunkProgress: Map<number, number>; // partNumber -> progress %
}

export const useBucketObjects = (
  bucketId: string | undefined,
  projectId: string,
  currentPath: string
) => {
  const queryClient = useQueryClient();
  const [uploadTasks, setUploadTasks] = useState<UploadTask[]>([]);
  const chunkedUploadsRef = useRef<Map<string, ChunkUploadState>>(new Map());

  // Dynamic upload configuration based on internet speed
  const [uploadConfig, setUploadConfig] = useState<UploadConfig>(UPLOAD_CONFIGS['fast']);
  const speedEstimatedRef = useRef(false);

  // Estimate internet speed on mount
  useEffect(() => {
    if (!speedEstimatedRef.current) {
      speedEstimatedRef.current = true;
      estimateInternetSpeed().then((speed) => {
        const config = getUploadConfig(speed);
        setUploadConfig(config);
        console.log(`Upload config: ${config.speedTier} (${speed.toFixed(1)} Mbps) - ${config.chunkSize / (1024 * 1024)}MB chunks, ${config.concurrency} parallel`);
      });
    }
  }, []);

  const { data: objectsData, isLoading } = useQuery({
    queryKey: ['objects', bucketId, currentPath],
    queryFn: () =>
      bucketService
        .listObjects(bucketId!, projectId, currentPath || undefined)
        .then((res) => res.data),
    enabled: !!bucketId && !!projectId,
  });

  const deleteObjectMutation = useMutation({
    mutationFn: (path: string) => bucketService.deleteObject(bucketId!, projectId, path),
    onSuccess: async () => {
      // Sync bucket stats to update object_count and current_size_bytes
      await bucketService.sync(bucketId!, projectId);
      queryClient.invalidateQueries({ queryKey: ['objects'] });
      queryClient.invalidateQueries({ queryKey: ['buckets'] });
      toast.success('Object deleted');
    },
    onError: () => {
      toast.error('Failed to delete object');
    },
  });

  // Calculate overall progress from chunk progress
  const calculateOverallProgress = useCallback((taskId: string): number => {
    const state = chunkedUploadsRef.current.get(taskId);
    if (!state) return 0;

    let totalProgress = 0;
    state.chunkProgress.forEach((progress) => {
      totalProgress += progress;
    });
    // Overall progress = (sum of all chunk progresses) / (totalChunks * 100) * 100
    return Math.round(totalProgress / state.totalChunks);
  }, []);

  // Upload a file using chunked/multipart upload
  // Uses dynamic chunk size and concurrency based on internet speed
  const uploadChunkedFile = useCallback(async (
    file: File,
    path: string,
    taskId: string
  ): Promise<boolean> => {
    try {
      // Use dynamic config based on estimated internet speed
      const { chunkSize, concurrency } = uploadConfig;

      // 1. Initiate multipart upload
      const initResponse = await bucketService.initiateMultipartUpload(
        bucketId!,
        projectId,
        path,
        file.type || 'application/octet-stream',
        file.size
      );

      const uploadId = initResponse.data.upload_id;
      // Use our calculated chunk size, but respect server's recommendation if smaller
      const effectiveChunkSize = Math.min(chunkSize, initResponse.data.chunk_size || chunkSize);
      const totalChunks = Math.ceil(file.size / effectiveChunkSize);
      const effectiveConcurrency = concurrency;

      // Initialize chunk upload state
      const state: ChunkUploadState = {
        uploadId,
        path,
        parts: [],
        totalChunks,
        completedChunks: 0,
        chunkProgress: new Map(),
      };
      chunkedUploadsRef.current.set(taskId, state);

      // 2. Split file into chunks and prepare upload tasks
      const chunkTasks: { partNumber: number; start: number; end: number }[] = [];
      for (let i = 0; i < totalChunks; i++) {
        const start = i * effectiveChunkSize;
        const end = Math.min(start + effectiveChunkSize, file.size);
        chunkTasks.push({ partNumber: i + 1, start, end });
      }

      // 3. Upload chunks in parallel with concurrency limit
      let chunkIndex = 0;
      const uploadedParts: { part_number: number; etag: string }[] = [];
      let hasError = false;

      const uploadNextChunk = async (): Promise<void> => {
        while (chunkIndex < chunkTasks.length && !hasError) {
          const currentIdx = chunkIndex++;
          const { partNumber, start, end } = chunkTasks[currentIdx];
          const chunk = file.slice(start, end);

          try {
            const response = await bucketService.uploadPart(
              bucketId!,
              projectId,
              uploadId,
              partNumber,
              chunk,
              path,
              (progress) => {
                // Update chunk progress
                const currentState = chunkedUploadsRef.current.get(taskId);
                if (currentState) {
                  currentState.chunkProgress.set(partNumber, progress);
                  // Update overall task progress
                  const overallProgress = calculateOverallProgress(taskId);
                  setUploadTasks((prev) =>
                    prev.map((task) =>
                      task.id === taskId ? { ...task, progress: overallProgress } : task
                    )
                  );
                }
              }
            );

            // Store completed part info
            uploadedParts.push({
              part_number: response.data.part_number,
              etag: response.data.etag,
            });

            // Update completed count
            const currentState = chunkedUploadsRef.current.get(taskId);
            if (currentState) {
              currentState.completedChunks++;
              currentState.chunkProgress.set(partNumber, 100);
            }
          } catch (error) {
            hasError = true;
            throw error;
          }
        }
      };

      // Start concurrent upload workers based on dynamic config
      const workers = Array(Math.min(effectiveConcurrency, totalChunks))
        .fill(null)
        .map(() => uploadNextChunk());

      await Promise.all(workers);

      if (hasError) {
        // Abort the multipart upload on error
        await bucketService.abortMultipartUpload(bucketId!, projectId, uploadId, path);
        return false;
      }

      // 4. Sort parts by part number and complete the upload
      uploadedParts.sort((a, b) => a.part_number - b.part_number);

      await bucketService.completeMultipartUpload(
        bucketId!,
        projectId,
        uploadId,
        path,
        uploadedParts
      );

      // Clean up state
      chunkedUploadsRef.current.delete(taskId);

      return true;
    } catch (error) {
      // Clean up state on error
      chunkedUploadsRef.current.delete(taskId);
      throw error;
    }
  }, [bucketId, projectId, calculateOverallProgress, uploadConfig]);

  // Internal upload function - returns taskId for tracking
  // Uses chunked upload for files larger than CHUNKED_UPLOAD_THRESHOLD
  const uploadSingleFile = useCallback(async (
    file: File,
    path: string,
    skipSync: boolean = false
  ): Promise<{ success: boolean; taskId: string }> => {
    const taskId = `${file.name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const useChunkedUpload = file.size > CHUNKED_UPLOAD_THRESHOLD;

    // Add new upload task
    const newTask: UploadTask = {
      id: taskId,
      fileName: file.name,
      fileSize: file.size,
      progress: 0,
      status: 'uploading',
    };

    setUploadTasks((prev) => [...prev, newTask]);

    try {
      if (useChunkedUpload) {
        // Use chunked upload for large files
        await uploadChunkedFile(file, path, taskId);
      } else {
        // Use regular upload for small files
        await bucketService.uploadObjectWithProgress(
          bucketId!,
          projectId,
          file,
          path,
          (progress) => {
            setUploadTasks((prev) =>
              prev.map((task) =>
                task.id === taskId ? { ...task, progress } : task
              )
            );
          }
        );
      }

      // Mark as completed
      setUploadTasks((prev) =>
        prev.map((task) =>
          task.id === taskId ? { ...task, status: 'completed', progress: 100 } : task
        )
      );

      if (!skipSync) {
        // Sync bucket stats to update object_count and current_size_bytes
        await bucketService.sync(bucketId!, projectId);
        queryClient.invalidateQueries({ queryKey: ['objects'] });
        queryClient.invalidateQueries({ queryKey: ['buckets'] });
      }

      return { success: true, taskId };
    } catch (error: any) {
      // Mark as failed
      setUploadTasks((prev) =>
        prev.map((task) =>
          task.id === taskId
            ? { ...task, status: 'failed', error: error?.message || 'Upload failed' }
            : task
        )
      );
      return { success: false, taskId };
    }
  }, [bucketId, projectId, queryClient, uploadChunkedFile]);

  // Upload single file with sync (for backwards compatibility)
  const uploadObjectWithProgress = useCallback(async (file: File, path: string) => {
    const result = await uploadSingleFile(file, path, false);
    return result.success;
  }, [uploadSingleFile]);

  // Upload multiple files in parallel with concurrency limit
  const uploadMultipleObjectsWithProgress = useCallback(async (
    files: { file: File; path: string }[],
    concurrencyLimit: number = 3
  ): Promise<{ successful: number; failed: number }> => {
    if (files.length === 0) return { successful: 0, failed: 0 };

    let successful = 0;
    let failed = 0;
    let index = 0;

    // Process files with concurrency limit
    const processNext = async (): Promise<void> => {
      while (index < files.length) {
        const currentIndex = index++;
        const { file, path } = files[currentIndex];
        const result = await uploadSingleFile(file, path, true); // Skip sync for each file
        if (result.success) {
          successful++;
        } else {
          failed++;
        }
      }
    };

    // Start concurrent workers up to the limit
    const workers = Array(Math.min(concurrencyLimit, files.length))
      .fill(null)
      .map(() => processNext());

    await Promise.all(workers);

    // Sync once after all uploads complete
    await bucketService.sync(bucketId!, projectId);
    queryClient.invalidateQueries({ queryKey: ['objects'] });
    queryClient.invalidateQueries({ queryKey: ['buckets'] });

    return { successful, failed };
  }, [bucketId, projectId, queryClient, uploadSingleFile]);

  const dismissUploadTask = useCallback((taskId: string) => {
    setUploadTasks((prev) => prev.filter((task) => task.id !== taskId));
  }, []);

  const dismissAllUploadTasks = useCallback(() => {
    setUploadTasks([]);
  }, []);

  // Legacy upload function (without progress)
  const uploadObject = async (file: File, path: string) => {
    try {
      await bucketService.uploadObject(bucketId!, projectId, file, path);
      toast.success(`Uploaded ${file.name}`);
      // Sync bucket stats to update object_count and current_size_bytes
      await bucketService.sync(bucketId!, projectId);
      queryClient.invalidateQueries({ queryKey: ['objects'] });
      queryClient.invalidateQueries({ queryKey: ['buckets'] });
      return true;
    } catch (error) {
      toast.error(`Failed to upload ${file.name}`);
      return false;
    }
  };

  const getDownloadUrl = async (obj: ObjectInfo) => {
    try {
      const response = await bucketService.getPresignedDownload(bucketId!, projectId, obj.key);
      return response.data.url;
    } catch (error) {
      toast.error('Failed to get download link');
      return null;
    }
  };

  return {
    objects: objectsData?.objects || objectsData || [],
    isLoading,
    deleteObject: deleteObjectMutation.mutate,
    uploadObject,
    uploadObjectWithProgress,
    uploadMultipleObjectsWithProgress,
    uploadTasks,
    dismissUploadTask,
    dismissAllUploadTasks,
    getDownloadUrl,
    // Upload configuration info (for UI display)
    uploadConfig: {
      speedTier: uploadConfig.speedTier,
      chunkSizeMB: uploadConfig.chunkSize / (1024 * 1024),
      concurrency: uploadConfig.concurrency,
    },
  };
};
